import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Save, PenLine, AlertCircle, Loader2 } from "lucide-react";

function InputField({ label, value, onChange, type = "number", prefix, step = "0.01", readOnly = false, required = false, testId }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{label}{required && <span className="text-red-500 ml-1">*</span>}</label>
      <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#FFDF00]">
        {prefix && <span className="px-3 py-2 bg-slate-50 text-slate-500 text-sm border-r border-slate-200">{prefix}</span>}
        <input
          type={type}
          step={step}
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          className={`flex-1 px-3 py-2 text-sm outline-none ${readOnly ? "bg-slate-50 text-slate-500" : "bg-white"}`}
          data-testid={testId}
        />
      </div>
    </div>
  );
}

// Fix WH66 (2026-09-29): la fecha de hoy en la hora del dispositivo.
// toISOString() da la fecha en UTC: después de las 6 p.m. de México ya es
// "mañana" y el corte de la noche quedaba con la fecha del día siguiente
// (Xalisco 25 y 26/09, Puerto Vallarta 29/09).
function hoyLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function OpsCorteNuevo() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(user?.branch_id || "");
  const [date, setDate] = useState(() => hoyLocal());  // Fix WH66 (2026-09-29)
  const [saving, setSaving] = useState(false);

  const [efectivo, setEfectivo] = useState({ ventas_brutas: "", retiros: "", deposito_caja_fuerte: "" });
  const [tarjeta, setTarjeta] = useState({ ventas_brutas: "", procesador: "CLIP", comision_estimada: "" });
  const [delivery, setDelivery] = useState({ rappi: "", uber_eats: "", didi: "", oxxo_pay: "" });
  const [totalOrdenes, setTotalOrdenes] = useState("");
  const [observaciones, setObservaciones] = useState("");

  const isAdmin = user?.role === "corporate_admin";

  useEffect(() => {
    if (isAdmin) {
      api.get("/branches").then(r => setBranches(r.data.filter(b => !b.is_corporate))).catch(() => {});
    }
  }, [isAdmin]);

  const n = v => parseFloat(v) || 0;

  const depositoCajaFuerte = n(efectivo.ventas_brutas) - n(efectivo.retiros);
  const comisionEstimada = Math.round(n(tarjeta.ventas_brutas) * 0.0168 * 100) / 100;
  const deliveryTotal = n(delivery.rappi) + n(delivery.uber_eats) + n(delivery.didi) + n(delivery.oxxo_pay);
  const ventaTotal = n(efectivo.ventas_brutas) + n(tarjeta.ventas_brutas) + deliveryTotal;
  const ticketPromedio = totalOrdenes > 0 ? ventaTotal / n(totalOrdenes) : 0;
  const fmtMXN = v => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v);

  const handleSave = async (sign = false) => {
    if (!branchId) { toast.error("Selecciona una sucursal"); return; }
    if (sign) {
      const confirmed = window.confirm("Una vez firmado, el corte no se puede editar. ¿Confirmar firma?");
      if (!confirmed) return;
    }
    setSaving(true);
    try {
      const body = {
        branch_id: branchId,
        date,
        channels: {
          efectivo: {
            ventas_brutas: n(efectivo.ventas_brutas),
            retiros: n(efectivo.retiros),
            deposito_caja_fuerte: depositoCajaFuerte,
          },
          tarjeta: {
            ventas_brutas: n(tarjeta.ventas_brutas),
            procesador: "CLIP",
            comision_estimada: comisionEstimada,
          },
          delivery: {
            rappi: n(delivery.rappi),
            uber_eats: n(delivery.uber_eats),
            didi: n(delivery.didi),
            oxxo_pay: n(delivery.oxxo_pay),
          },
        },
        venta_total: ventaTotal,
        total_ordenes: n(totalOrdenes),
        total_personas: n(totalOrdenes),
        ticket_promedio: Math.round(ticketPromedio * 100) / 100,
        observaciones: observaciones || null,
      };
      const res = await api.post("/ops/cortes", body);
      const corteId = res.data.id;
      if (sign) {
        await api.post(`/ops/cortes/${corteId}/sign`);
        toast.success("Corte guardado y firmado");
      } else {
        toast.success("Corte guardado como borrador");
      }
      navigate(`/ops/cortes/${corteId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar el corte");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/ops/cortes")} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
          <ChevronLeft className="w-5 h-5 text-slate-500" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Nuevo Corte de Caja</h1>
          <p className="text-sm text-slate-500">Registra las ventas del dia</p>
        </div>
      </div>

      {/* Branch + Date */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h2 className="font-semibold text-slate-900">Datos generales</h2>
        <div className="grid grid-cols-2 gap-4">
          {isAdmin ? (
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Sucursal <span className="text-red-500">*</span></label>
              <select
                value={branchId}
                onChange={e => setBranchId(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                data-testid="corte-branch-select"
              >
                <option value="">Seleccionar...</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Sucursal</label>
              <div className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-slate-50 text-slate-700">
                {user?.full_name || "Mi sucursal"}
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Fecha <span className="text-red-500">*</span></label>
            <input
              type="date"
              value={date}
              max={hoyLocal()}
              onChange={e => setDate(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
              data-testid="corte-date-input"
            />
          </div>
        </div>
      </div>

      {/* Efectivo */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h2 className="font-semibold text-slate-900">Efectivo</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <InputField label="Ventas brutas" value={efectivo.ventas_brutas} onChange={e => setEfectivo(p => ({ ...p, ventas_brutas: e.target.value }))} prefix="$" testId="efectivo-ventas-brutas" />
          <InputField label="Retiros" value={efectivo.retiros} onChange={e => setEfectivo(p => ({ ...p, retiros: e.target.value }))} prefix="$" testId="efectivo-retiros" />
          <InputField label="Deposito caja fuerte" value={depositoCajaFuerte.toFixed(2)} readOnly prefix="$" testId="efectivo-deposito" />
        </div>
      </div>

      {/* Tarjeta */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h2 className="font-semibold text-slate-900">Tarjeta (CLIP)</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InputField label="Ventas brutas" value={tarjeta.ventas_brutas} onChange={e => setTarjeta(p => ({ ...p, ventas_brutas: e.target.value }))} prefix="$" testId="tarjeta-ventas-brutas" />
          <InputField label="Comision estimada (1.68%)" value={comisionEstimada.toFixed(2)} readOnly prefix="$" testId="tarjeta-comision" />
        </div>
      </div>

      {/* Delivery */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h2 className="font-semibold text-slate-900">Delivery</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <InputField label="Rappi" value={delivery.rappi} onChange={e => setDelivery(p => ({ ...p, rappi: e.target.value }))} prefix="$" testId="delivery-rappi" />
          <InputField label="Uber Eats" value={delivery.uber_eats} onChange={e => setDelivery(p => ({ ...p, uber_eats: e.target.value }))} prefix="$" testId="delivery-uber" />
          <InputField label="DiDi" value={delivery.didi} onChange={e => setDelivery(p => ({ ...p, didi: e.target.value }))} prefix="$" testId="delivery-didi" />
          <InputField label="OXXO Pay" value={delivery.oxxo_pay} onChange={e => setDelivery(p => ({ ...p, oxxo_pay: e.target.value }))} prefix="$" testId="delivery-oxxo" />
        </div>
      </div>

      {/* Resumen */}
      <div className="bg-slate-900 text-white rounded-xl p-5 space-y-3">
        <h2 className="font-semibold">Resumen</h2>
        <div className="grid grid-cols-3 gap-4">
          <div><div className="text-xs text-slate-400">Venta total</div><div className="text-xl font-bold">{fmtMXN(ventaTotal)}</div></div>
          <div>
            <div className="text-xs text-slate-400">Total ordenes</div>
            <input
              type="number"
              value={totalOrdenes}
              onChange={e => setTotalOrdenes(e.target.value)}
              className="w-full bg-slate-800 rounded-lg px-2 py-1 text-white text-sm outline-none focus:ring-2 focus:ring-[#FFDF00] mt-1"
              placeholder="0"
              data-testid="total-ordenes-input"
            />
          </div>
          <div><div className="text-xs text-slate-400">Ticket promedio</div><div className="text-xl font-bold">{totalOrdenes ? fmtMXN(ticketPromedio) : "—"}</div></div>
        </div>
        <div>
          <label className="text-xs text-slate-400">Observaciones (opcional)</label>
          <textarea
            value={observaciones}
            onChange={e => setObservaciones(e.target.value)}
            rows={2}
            className="w-full mt-1 bg-slate-800 rounded-lg px-3 py-2 text-white text-sm outline-none focus:ring-2 focus:ring-[#FFDF00] resize-none"
            placeholder="Notas del dia..."
            data-testid="observaciones-input"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={() => handleSave(false)}
          disabled={saving}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border border-slate-300 font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
          data-testid="save-draft-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Guardar borrador
        </button>
        <button
          onClick={() => handleSave(true)}
          disabled={saving}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50"
          data-testid="save-and-sign-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}
          Guardar y firmar
        </button>
      </div>

      <div className="flex items-center gap-2 text-xs text-slate-400">
        <AlertCircle className="w-3.5 h-3.5" />
        Una vez firmado, el corte no puede editarse
      </div>
    </div>
  );
}
